import {
  API,
  currentEnv,
  encodeURLString,
  ErrorHandling,
  EVENT_APP_INITIAL_LOADING_COMPLETE,
  EVENT_REQUEST_APP_AVATAR_STATE,
  EVENT_REQUEST_EXIT_MONSTER_WORLD,
  FatalErrors,
  FeatureSwitchManager,
  fetchWrapper,
  getLauncherStartupOptions,
  HandlingMethod,
  isIOSAppEnv,
  Logger,
  patchFetch,
  sharedErrorHandlingCases,
} from "@classdojo/monsterverse-utils";

import { getAWSHostURLs, handleUserDataRequests } from "./AWSHostURLs";
import { handleIFrameMessages, postToApp } from "./MessageHandler";
import { Networking } from "./Networking";
import { fatalErrorIfOutage, OutageError } from "./Outage";
import { shim } from "./Shim";
import { handleFatalErrors } from "./UI/FatalErrorModal";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let networking: Networking | undefined;

// eslint-disable-next-line sonarjs/cognitive-complexity
async function main() {
  Logger.initialize(FeatureSwitchManager, { env: currentEnv(), customEndpoint: "/logs" });
  const returnUserToApp = () => postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD });
  ErrorHandling.initialize()
    .registerErrorCase("Old IOS clients with non fatal error in resizeIfNeeded function", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return (
          error.message === "undefined is not an object (evaluating 'pc.app.graphicsDevice.canvas')" &&
          error.stack !== undefined &&
          error.stack.includes("resizeIfNeeded")
        );
      },
    })
    .registerErrorCase("Outage", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return error instanceof OutageError;
      },
    })
    .registerErrorCase("Fatal error caught during loading; sending user back to app.", {
      method: HandlingMethod.HandleError,
      isCondition: () => {
        return true;
      },
      onHandle: () => {
        returnUserToApp();
      },
    })
    .registerErrorCases(
      ...sharedErrorHandlingCases(() => {
        returnUserToApp();
      })
    );
  shim();
  patchFetch();
  API.init("https://clients.multiplayer.classdojo.com", getLauncherStartupOptions().entityType, {
    Logger,
    ErrorHandling,
    FatalErrors,
    fetchWrapper,
  });

  const loadingEl = document.getElementById("application-launcher-splash-wrapper");
  if (!loadingEl) {
    return;
  }
  const iframeEl = document.getElementById("application-iframe");
  if (!(iframeEl instanceof HTMLIFrameElement)) {
    return;
  }
  const pcAppEventHandler = handleIFrameMessages(iframeEl, loadingEl);
  handleFatalErrors(iframeEl, pcAppEventHandler);
  handleUserDataRequests(iframeEl);
  await fatalErrorIfOutage();

  const loadClient = async () => {
    const { entityType, classId, startupParams } = getLauncherStartupOptions();
    Logger.setEntityType(entityType === "student" ? "studentUser" : entityType);

    const requestIsland = async (newIslandID?: string) => {
      const { wsUrl, hubUrl, island, countryCode, serverRegion, desiredServerRegion } = await getAWSHostURLs(
        newIslandID ?? classId ?? "default",
        entityType
      );
      Logger.setServerID(island.id);
      Logger.setCountryCode(countryCode);

      const hubUrlBuilder = new URL(hubUrl);
      hubUrlBuilder.searchParams.set("data", encodeURLString(JSON.stringify({ island, entityType, countryCode })));
      hubUrlBuilder.searchParams.set("sessionId", Logger.getSessionId());
      hubUrlBuilder.searchParams.set("startupParams", startupParams);

      const selfParams = new URL(window.location.href).searchParams;
      const bundleOverride = selfParams.get("bundle");
      if (bundleOverride) {
        hubUrlBuilder.searchParams.set("bundle", bundleOverride);
      }

      //Load iframe client
      iframeEl.src = hubUrlBuilder.toString();

      return { wsUrl, countryCode, serverRegion, desiredServerRegion };
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    networking = new Networking(iframeEl, pcAppEventHandler, requestIsland);
  };

  //Client loading happens after the IOS host is ready
  //This ensures the host has registered the handshake messages before we try and fire them
  if (isIOSAppEnv()) {
    let appAvatarStateRegistered = false;
    let appInitialLoadingRegistered = false;
    pcAppEventHandler.onRegistration(EVENT_REQUEST_APP_AVATAR_STATE, () => {
      appAvatarStateRegistered = true;
      if (appInitialLoadingRegistered) {
        loadClient();
      }
    });
    pcAppEventHandler.onRegistration(EVENT_APP_INITIAL_LOADING_COMPLETE, () => {
      appInitialLoadingRegistered = true;
      if (appAvatarStateRegistered) {
        loadClient();
      }
    });
  } else {
    loadClient();
  }
}

main();
